import { useEffect, useRef } from "react";
import L, { LatLngExpression } from "leaflet";
import { useMap } from "react-leaflet";
import { MapBounds } from "../../../../features/results/ResultsOperationalUnits/useOperationalUnitsData";

export const MapViewUpdater = ({
	isLoading,
	lastUserZoomAt,
	currentZoom,
	setCurrentZoom,
	isProgrammaticMove,
	isFullscreen,
	mapBounds,
	internalMapCenter,
	center,
}: {
	isLoading?: boolean;
	lastUserZoomAt: React.RefObject<number | null>;
	currentZoom: number;
	setCurrentZoom: (zoom: number) => void;
	isProgrammaticMove: React.RefObject<boolean>;
	isFullscreen: boolean;
	mapBounds?: MapBounds;
	internalMapCenter: React.RefObject<LatLngExpression | null>;
	center?: LatLngExpression;
}) => {
	const map = useMap();
	const lastApplied = useRef<MapBounds | null>(null);
	const lastFullscreen = useRef<boolean>(isFullscreen);
	const lastCenter = useRef<L.LatLng | null>(null);

	useEffect(() => {
		if (!map || !center || isLoading || mapBounds) return;

		const newCenter = L.latLng(center);

		// check if the new center is not the same as before
		if (lastCenter.current?.equals(newCenter)) return;

		// avoid moving the map immediately after a user zoom/move
		const zoomRecently = lastUserZoomAt.current && Date.now() - lastUserZoomAt.current < 800;

		if (zoomRecently) return;

		lastCenter.current = newCenter;

		isProgrammaticMove.current = true;

		map.setView(newCenter, currentZoom, {
			animate: true,
			duration: 0.25,
		});

		// Clear programmatic flag after movement ends
		const clearFlag = () => (isProgrammaticMove.current = false);

		map.once("moveend", clearFlag);

		return () => {
			map.off("moveend", clearFlag);
		};
	}, [center]);

	useEffect(() => {
		if (!map) return;

		// Handle fullscreen changes
		if (lastFullscreen.current !== isFullscreen) {
			const clearFlag = () => (isProgrammaticMove.current = false);

			// After DOM updates, restore the center
			const timeout = setTimeout(() => {
				lastFullscreen.current = isFullscreen;
				if (!internalMapCenter.current) return;

				isProgrammaticMove.current = true;

				// Smoothly pan to previous center
				map.panTo(internalMapCenter.current, { animate: false });

				// Clear programmatic flag after movement ends
				map.once("moveend", clearFlag);
			}, 100);

			// Fallback in case events are not triggered
			return () => {
				clearTimeout(timeout);
				map.off("moveend", clearFlag);
				map.off("zoomend", clearFlag);
			};
		}
	}, [isFullscreen, map]);

	useEffect(() => {
		if (!map || !mapBounds) return;

		// If the map is still loading, just remember the bounds and do nothing
		// This prevents a jump when new data arrives
		if (isLoading) {
			lastApplied.current = mapBounds;
			return;
		}

		// Avoid moving the map immediately after a user zoom/move
		const zoomRecently = lastUserZoomAt.current && Date.now() - lastUserZoomAt.current < 800;
		if (zoomRecently) return;

		const last = lastApplied.current;
		const tolerance = 1e-6;
		const zoomTolerance = 0.1;

		// Check if the new bounds are essentially the same as the last applied
		const isSameBounds =
			last &&
			Math.abs(last.min_lat - mapBounds.min_lat) < tolerance &&
			Math.abs(last.max_lat - mapBounds.max_lat) < tolerance &&
			Math.abs(last.min_lon - mapBounds.min_lon) < tolerance &&
			Math.abs(last.max_lon - mapBounds.max_lon) < tolerance &&
			Math.abs((last.zoom ?? 0) - (mapBounds.zoom ?? 0)) < zoomTolerance;

		if (isSameBounds) return;

		// Calculate new center from bounds
		const southWest = L.latLng(mapBounds.min_lat, mapBounds.min_lon);
		const northEast = L.latLng(mapBounds.max_lat, mapBounds.max_lon);
		const newBounds = L.latLngBounds(southWest, northEast);
		const newCenter = newBounds.getCenter();

		const currentCenter = map.getCenter();
		const distance = currentCenter.distanceTo(newCenter); // in meters
		const zoomDiff = Math.abs((mapBounds.zoom ?? currentZoom) - map.getZoom());

		// Decide whether the map actually needs to move or zoom
		const shouldMove = distance > 100; // move only if center changes significantly
		const shouldZoom = zoomDiff > 0.3; // zoom only if change is noticeable

		if (!shouldMove && !shouldZoom) return;

		// Set flag to indicate programmatic move (avoid triggering onBoundsChange)
		isProgrammaticMove.current = true;
		lastApplied.current = mapBounds;

		// Smoothly pan to new center
		map.panTo(newCenter, { animate: true, duration: 0.2 });

		// Smoothly set new zoom
		map.setZoom(mapBounds.zoom ?? currentZoom, { animate: true });

		// Update local zoom state
		setCurrentZoom(mapBounds.zoom ?? currentZoom);

		// Clear programmatic flag after movement ends
		const clearFlag = () => (isProgrammaticMove.current = false);
		map.once("moveend", clearFlag);
		map.once("zoomend", clearFlag);

		// Fallback in case events are not triggered
		const timeout = setTimeout(clearFlag, 500);
		return () => {
			clearTimeout(timeout);
			map.off("moveend", clearFlag);
			map.off("zoomend", clearFlag);
		};
	}, [mapBounds, map]);

	return null;
};
